import { MinusCircleOutlined, PlusOutlined } from '@ant-design/icons'
import { Button, DatePicker, Form, Input, InputNumber, message, Modal } from 'antd'
import { useState } from 'react'

import { AppAdmin } from 'api/app-admin'
import ProtectedButton from 'component/button/protected-button'
import { FEATURE } from 'constants/feature'
import { useVestingContextV2 } from 'context/vesting-context-provider-v2'
import { isValidETHAddress } from 'utils/validate-fields'

interface IButtonCreate {
  onSubmit?: () => void
}

const ButtonCreate = ({ onSubmit }: IButtonCreate) => {
  const { type } = useVestingContextV2()

  const [openModal, setOpenModal] = useState(false)
  const [form] = Form.useForm()
  const [messageApi, contextHolder] = message.useMessage()

  const handleOk = () => {
    form
      .validateFields()
      .then(async (values: any) => {
        const transformedData = (values?.data || []).map((item: any) => ({
          ownerAddress: item.ownerAddress,
          amount: Number(item.amount),
          signDate: item.signDate?.unix(),
        }))

        const createData = {
          type: values.type,
          data: transformedData,
        }

        const response = await AppAdmin.createUserVestingV2(createData)
        setOpenModal(false)
        form.resetFields()
        messageApi.open({
          type: 'success',
          content: response.data.message,
        })
        onSubmit?.()
      })
      .catch((error: any) => {
        messageApi.open({
          type: 'error',
          content: error.message ? error.message : error.errorFields?.[0]?.errors?.[0],
        })
        if (error && error.message) {
          form.setFields([
            {
              name: error.message.split(' ')[0],
              errors: [error.message],
            },
          ])
        }
      })
  }

  const handleCancel = () => {
    setOpenModal(false)
    form.resetFields()
  }

  return (
    <>
      {contextHolder}
      <ProtectedButton
        color="primary"
        variant="solid"
        type="primary"
        feature={FEATURE.VESTING}
        requireCRUD
        onClick={() => {
          if (type) {
            setOpenModal(true)
            form.setFieldsValue({
              type,
              data: [{ ownerAddress: '', amount: null, signDate: null }],
            })
          }
        }}
      >
        Create
      </ProtectedButton>
      <Modal
        title="Create User Vesting V2"
        open={openModal}
        onOk={handleOk}
        onCancel={handleCancel}
        className="modalStaking"
        width={900}
        centered
        okText="Create"
      >
        <Form form={form} style={{ maxWidth: 900 }}>
          <Form.Item name="type" hidden>
            <Input disabled />
          </Form.Item>
          <Form.List name="data">
            {(fields, { add, remove }) => (
              <>
                {fields.map(({ key, name, ...restField }) => (
                  <div
                    key={key}
                    style={{
                      display: 'flex',
                      columnGap: '8px',
                      width: '100%',
                      marginBottom: '16px',
                    }}
                  >
                    <Form.Item
                      {...restField}
                      name={[name, 'ownerAddress']}
                      style={{ marginBottom: 0, width: '45%' }}
                      rules={[
                        {
                          required: true,
                          message: 'Please input user address!',
                        },
                        {
                          validator(_, value) {
                            if (!value) {
                              return Promise.resolve()
                            }
                            if (isValidETHAddress(value) === false) {
                              return Promise.reject('Invalid Address')
                            }
                            const list = form.getFieldValue('data') || []
                            const count = list.filter(
                              (item: any) =>
                                item?.ownerAddress?.toLowerCase() === value?.toLowerCase()
                            ).length
                            if (count > 1) {
                              return Promise.reject('Duplicate address found!')
                            }
                            return Promise.resolve()
                          },
                        },
                      ]}
                    >
                      <Input placeholder="User address" />
                    </Form.Item>
                    <Form.Item
                      {...restField}
                      name={[name, 'amount']}
                      style={{ marginBottom: 0, width: '25%' }}
                      rules={[
                        {
                          required: true,
                          message: 'Please input allocation!',
                        },
                      ]}
                    >
                      <InputNumber<number>
                        style={{ width: '100%' }}
                        min={0}
                        placeholder="Enter Allocation"
                      />
                    </Form.Item>
                    <Form.Item
                      {...restField}
                      name={[name, 'signDate']}
                      style={{ marginBottom: 0, width: '25%' }}
                      rules={[
                        {
                          required: true,
                          message: 'Please select sign date!',
                        },
                      ]}
                    >
                      <DatePicker
                        style={{ width: '100%' }}
                        showTime={{ format: 'HH:mm' }}
                        format="YYYY-MM-DD HH:mm"
                        placeholder="Select sign date"
                      />
                    </Form.Item>
                    <MinusCircleOutlined onClick={() => remove(name)} />
                  </div>
                ))}
                {fields.length < 10 ? (
                  <Form.Item>
                    <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                      Add field
                    </Button>
                  </Form.Item>
                ) : (
                  <div style={{ textAlign: 'center', color: 'red', marginTop: 8 }}>
                    Maximum 10 records allowed.
                  </div>
                )}
              </>
            )}
          </Form.List>
        </Form>
      </Modal>
    </>
  )
}

export default ButtonCreate
